import { useState, useEffect, useMemo } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

// FIN-3 — DefinirDatasModal.
//
// Form completo: Nome, Bandeira (visual-only — não persistida no model),
// Limite, Closing day, Due day. Validação client-side ANTES do PUT.
//
// Optimistic update via hook.updateAccount (que faz patch local antes
// do refetch). Rollback se erro do servidor.

const BANDEIRAS = ["Mastercard", "Visa", "Elo", "Amex", "Outro"];

function _toCents(v) {
  // Aceita "1234,56" ou "1234.56" pt-BR — retorna float ou null.
  if (v == null || v === "") return null;
  const s = String(v).replace(/\./g, "").replace(",", ".").replace(/[^0-9.\-]/g, "");
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : null;
}

function _formatBRLForInput(v) {
  if (v == null) return "";
  return Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function ValidationMsg({ children }) {
  return <p className="text-micro text-status-error mt-1" role="alert">{children}</p>;
}

export default function DefinirDatasModal({ open, onOpenChange, account, onSave }) {
  const [name, setName] = useState("");
  const [bandeira, setBandeira] = useState("Mastercard");
  const [limite, setLimite] = useState("");
  const [closingDay, setClosingDay] = useState("");
  const [dueDay, setDueDay] = useState("");
  const [saving, setSaving] = useState(false);
  const [touched, setTouched] = useState({});

  // Pre-preenche quando abre / muda account
  useEffect(() => {
    if (open && account) {
      setName(account.name || "");
      setBandeira(account.brand || "Mastercard");
      setLimite(_formatBRLForInput(account.credit_limit));
      setClosingDay(account.closing_day != null ? String(account.closing_day) : "");
      setDueDay(account.due_day != null ? String(account.due_day) : "");
      setSaving(false);
      setTouched({});
    }
  }, [open, account]);

  const errors = useMemo(() => {
    const out = {};
    const nameTrim = (name || "").trim();
    if (!nameTrim) out.name = "Informe um nome";
    else if (nameTrim.length > 50) out.name = "Máx 50 caracteres";

    const lim = _toCents(limite);
    if (limite !== "" && (lim == null || lim < 0)) out.limite = "Limite deve ser ≥ 0";

    const cd = parseInt(closingDay, 10);
    const dd = parseInt(dueDay, 10);
    if (!Number.isFinite(cd) || cd < 1 || cd > 31) out.closingDay = "Dia 1-31";
    if (!Number.isFinite(dd) || dd < 1 || dd > 31) out.dueDay = "Dia 1-31";
    if (Number.isFinite(cd) && Number.isFinite(dd) && cd === dd) {
      out.dueDay = "Fechamento e vencimento não podem ser iguais";
    }
    return out;
  }, [name, limite, closingDay, dueDay]);

  const hasErrors = Object.keys(errors).length > 0;
  const showErr = (key) => touched[key] && errors[key];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ name: true, limite: true, closingDay: true, dueDay: true });
    if (hasErrors || !account) return;

    setSaving(true);
    try {
      const patch = {
        name: name.trim(),
        closing_day: parseInt(closingDay, 10),
        due_day: parseInt(dueDay, 10),
      };
      const lim = _toCents(limite);
      if (lim != null) patch.credit_limit = lim;
      // Bandeira não vai pro backend (Account model não tem campo). Mantém só
      // visual no frontend via prop futura.
      await onSave(account.id, patch);
      toast.success("Cartão atualizado");
      onOpenChange(false);
    } catch (err) {
      toast.error(err.message || "Falha ao salvar. Tente novamente.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-surface-overlay border border-subtle max-w-md"
        data-testid="contas-modal-definir-datas"
      >
        <DialogHeader>
          <DialogTitle data-testid="contas-modal-title">
            Configurar cartão {account?.institution || account?.name || ""}
          </DialogTitle>
          <DialogDescription className="text-foreground-muted text-caption">
            Defina nome, bandeira, limite e datas pra o Nexo calcular fatura e vencimento.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} noValidate>
          <div className="space-y-4 py-2">
            {/* Nome */}
            <div>
              <label htmlFor="def-name" className="block text-caption font-medium text-foreground-muted mb-1">
                Nome
              </label>
              <Input
                id="def-name"
                type="text"
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={() => setTouched(t => ({ ...t, name: true }))}
                placeholder="Ex: C6 Black, Nubank Roxinho"
                maxLength={60}
                aria-invalid={Boolean(showErr("name"))}
                data-testid="contas-modal-input-name"
              />
              {showErr("name") && <ValidationMsg>{errors.name}</ValidationMsg>}
            </div>

            {/* Bandeira */}
            <div>
              <label className="block text-caption font-medium text-foreground-muted mb-1.5">
                Bandeira
              </label>
              <div className="flex flex-wrap gap-2">
                {BANDEIRAS.map(b => (
                  <button
                    key={b}
                    type="button"
                    onClick={() => setBandeira(b)}
                    className={cn(
                      "px-3 py-1.5 rounded-md text-caption font-medium border transition-colors",
                      bandeira === b
                        ? "bg-brand-cyan/15 border-brand-cyan/40 text-brand-cyan"
                        : "bg-transparent border-subtle text-foreground-muted hover:bg-surface-card-hover hover:text-foreground",
                    )}
                    data-testid={`contas-modal-brand-${b.toLowerCase()}`}
                  >
                    {b}
                  </button>
                ))}
              </div>
              <p className="text-micro text-foreground-subtle mt-1.5">
                Bandeira é apenas visual — backend ainda não persiste.
              </p>
            </div>

            {/* Limite */}
            <div>
              <label htmlFor="def-limite" className="block text-caption font-medium text-foreground-muted mb-1">
                Limite total (R$)
              </label>
              <Input
                id="def-limite"
                type="text"
                inputMode="decimal"
                value={limite}
                onChange={(e) => setLimite(e.target.value)}
                onBlur={() => setTouched(t => ({ ...t, limite: true }))}
                placeholder="5.000,00"
                aria-invalid={Boolean(showErr("limite"))}
                data-testid="contas-modal-input-limite"
              />
              {showErr("limite") && <ValidationMsg>{errors.limite}</ValidationMsg>}
            </div>

            {/* Closing + Due grid 2col */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="def-closing" className="block text-caption font-medium text-foreground-muted mb-1">
                  Dia de fechamento
                </label>
                <Input
                  id="def-closing"
                  type="number"
                  min="1"
                  max="31"
                  value={closingDay}
                  onChange={(e) => setClosingDay(e.target.value)}
                  onBlur={() => setTouched(t => ({ ...t, closingDay: true }))}
                  placeholder="15"
                  aria-invalid={Boolean(showErr("closingDay"))}
                  data-testid="contas-modal-input-closing"
                />
                {showErr("closingDay") && <ValidationMsg>{errors.closingDay}</ValidationMsg>}
              </div>
              <div>
                <label htmlFor="def-due" className="block text-caption font-medium text-foreground-muted mb-1">
                  Dia de vencimento
                </label>
                <Input
                  id="def-due"
                  type="number"
                  min="1"
                  max="31"
                  value={dueDay}
                  onChange={(e) => setDueDay(e.target.value)}
                  onBlur={() => setTouched(t => ({ ...t, dueDay: true }))}
                  placeholder="25"
                  aria-invalid={Boolean(showErr("dueDay"))}
                  data-testid="contas-modal-input-due"
                />
                {showErr("dueDay") && <ValidationMsg>{errors.dueDay}</ValidationMsg>}
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={saving}
              data-testid="contas-modal-cancel"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={saving || hasErrors}
              data-testid="contas-modal-submit"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Salvar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
